import { ApiError } from '@echo/api-client/client';
import { groupProgress, markExerciseDone, undoExerciseMark } from '@echo/api-client/echo';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SubGroupProgress } from '../content/member-progress';
import { SM_KIND } from '../content/schemas';
import type { ExerciseKind } from '../domain/exercise';
import { queryKeys } from '../query/keys';
import { useConfig, useSession } from './provider';
import { todayIso } from './use-today';

/**
 * One of the person's groups and how its members are doing this week, from echo's backend
 * (`content/member-progress.ts`): it reads the members' progress as the Stronger Memory group,
 * which the app can't. Leaders get each day exercise by exercise. Off where the environment has
 * no backend, and where the screen asking doesn't show progress (`enabled`).
 */
export function useSubGroupProgress(groupActorUuid: string, enabled = true) {
  const session = useSession();
  const { echoApiUrl } = useConfig();
  const today = todayIso();
  return useQuery({
    queryKey: queryKeys.subGroupProgress(groupActorUuid, today),
    enabled: enabled && session.status === 'authenticated' && echoApiUrl !== undefined,
    staleTime: 60_000,
    // A refusal stays a refusal: Core hides a group's members from those who don't lead it
    // (`MEMBERS_HIDDEN`), and asking twice more only kept the Groups card spinning for seconds.
    retry: (failures, error) => !(error instanceof ApiError && error.status < 500) && failures < 2,
    queryFn: async (): Promise<SubGroupProgress> =>
      (await groupProgress(groupActorUuid, { today })).data as SubGroupProgress,
  });
}

/**
 * A leader marking a member's exercise done today, or undoing their own group's mark. The
 * backend writes it as the Stronger Memory group; the member's app counts it on its next read.
 */
export function useMarkExercise(groupActorUuid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      memberActorUuid,
      kind,
      done,
    }: {
      memberActorUuid: string;
      kind: ExerciseKind;
      done: boolean;
    }) => {
      const today = todayIso();
      const call = done ? markExerciseDone : undoExerciseMark;
      return (await call(groupActorUuid, memberActorUuid, today, SM_KIND[kind])).data;
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sub-group-progress', groupActorUuid] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.groupDashboards() }),
      ]),
  });
}
